import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { ShieldOff } from 'lucide-react';
import type { Level, PageKey } from '@shared/permissions.ts';
import { useAuth } from './AuthProvider';
import { usePermissions } from './usePermissions';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';

export function FullPageSpinner() {
  return (
    <div className="flex min-h-dvh items-center justify-center" role="status" aria-label="טוען">
      <span className="size-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
    </div>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { loading, user, claims, signOut } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageSpinner />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (!claims || claims.a !== 1) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-4">
        <EmptyState icon={ShieldOff} title="החשבון עדיין לא פעיל" action={<Button variant="outline" onClick={() => signOut()}>יציאה</Button>}>
          המשתמש קיים אבל עדיין לא הוגדרו לו הרשאות, או שהוא הושבת. פנו למנהל המערכת.
        </EmptyState>
      </div>
    );
  }
  return <>{children}</>;
}

export function RequirePage({ page, min = 1, children }: { page: PageKey; min?: Level; children: ReactNode }) {
  const { level } = usePermissions();
  if (level(page) < min) return <NoAccess />;
  return <>{children}</>;
}

/** The alerts center: financial readers only (canSeeAlerts). */
export function RequireAlerts({ children }: { children: ReactNode }) {
  const { seeAlerts } = usePermissions();
  if (!seeAlerts) return <NoAccess />;
  return <>{children}</>;
}

/** "קופת גזבר" — only its users (claim t); the rules enforce it, this only avoids an empty page. */
export function RequireTreasurer({ children }: { children: ReactNode }) {
  const { treasurer } = usePermissions();
  if (!treasurer) return <NoAccess />;
  return <>{children}</>;
}

export function NoAccess() {
  return (
    <EmptyState icon={ShieldOff} title="אין לכם גישה לעמוד הזה">
      אם אתם צריכים גישה, פנו למנהל המערכת.
    </EmptyState>
  );
}
